import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Retry } from '@components/common';
import { metricSelector } from '@store/metricsChartContainer/selectors';
import { setMetrics } from '@store/metricsChartContainer/actions';
import styles from './index.less';
import Charts from './Charts';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  activeTabAtom,
  activeToolAtom,
  diagnosticsModeAtom,
  horizontalLayoutAtom,
  panelStyleAtom,
  Tabs,
  widgetPanelVisibilityAtom,
} from '../atoms';
import { tid } from '@utils/test';
import { useFetchDod } from '../hooks/useDodSeries';
import useFetchAIAlerts from '../hooks/alerts/useFetchAIAlerts';
import useFetchManualAlerts from '@components/metrics-panel-hz/hooks/alerts/useFetchManualAlerts';
import { useFetchMetricSeries } from '../hooks/useMetricSeries';
import useQueryMetrics from '../hooks/useQueryMetrics';
import { useFetchMetricFilterSeries } from '../hooks/useMetricFilterSeries';
import { metricPanelAtom } from '@src/atoms/panels';
import ErrorBoundary from '@components/common/Retry/ErrorBoundary';
import useLiveSummary from '../hooks/useLiveSummary';
import { useFetchCompare } from '../hooks/useCompareSeries';
import ExportCompareFilters from './ExportCompareFilters';
import { isCDNAccountSelector, widgetPanelFeatureSelector } from '@src/store/common/selectors';
import { BetaTag, MetricChartsWrapper, StyledTabs, TabWrapper } from './styled';
import { getChartStyle } from '../control-panel/PanelResize';
import StateTags from '../control-panel/StateTags';
import useAutoScroll from '../hooks/useAutoScroll';
import useLoadingState from '../hooks/useLoadingState';
import { useECOServerErrorMessage } from '@hooks/eco/useECOQueryError';
import { isAppModule, isEiModule } from '@src/utils/constants';
import { Radio } from 'antd';
import WidgetsPanelPlaceholder from './WidgetsPanelPlaceholder';
import { perfTrack } from '@utils/sensor/track';
import WidgetPanel from '@src/components/widget-panel';
import useTabSwitch from '../hooks/useTabSwitch';
import { metricsContainerRefAtom } from '@src/atoms/page';

// TODO: move out as index
function PanelTabs() {
  const { panelWidth } = useAtomValue(panelStyleAtom);
  const horizontalLayout = useAtomValue(horizontalLayoutAtom);
  const height = horizontalLayout ? 'auto' : '100%';
  const [activeTab, setActiveTab] = useAtom(activeTabAtom);
  const widgetPanelVisibility = useAtomValue(widgetPanelVisibilityAtom);
  const widgetPanelEnabled = useSelector(widgetPanelFeatureSelector);
  const isCDNAccount = useSelector(isCDNAccountSelector);

  const { widgetPanelSwitchCleanup, metricPanelSwitchCleanup } = useTabSwitch();

  const showWidgetTab = (isAppModule || isEiModule) && widgetPanelVisibility;

  const handleChange = (event: any) => {
    const activeKey = event.target.value;
    if (activeKey === Tabs.Widgets) {
      widgetPanelSwitchCleanup();
    } else if (activeKey === Tabs.MetricCharts) {
      metricPanelSwitchCleanup();
    }
    setActiveTab(activeKey);
  };

  const tabList = [
    {
      key: Tabs.Widgets.toString(),
      label: <div style={{ paddingRight: 10 }}>Summary</div>,
      // show coming soon if panel is not enabled
      children: widgetPanelEnabled && !isCDNAccount ? <WidgetPanel /> : <WidgetsPanelPlaceholder />,
      disabled: !showWidgetTab,
      value: Tabs.Widgets,
    },
    {
      key: Tabs.MetricCharts.toString(),
      label: 'Analysis',
      children: <MetricsPanel />,
      disabled: false,
      value: Tabs.MetricCharts,
    },
  ];

  // for custom tab style
  const renderTabBar = () => {
    return (
      <TabWrapper>
        <Radio.Group
          options={tabList}
          onChange={handleChange}
          value={activeTab}
          optionType="button"
          buttonStyle="solid"
        />
        <StateTags />
      </TabWrapper>
    );
  };

  return (
    <div
      className={styles.metricsContainer}
      {...tid('metrics-panel')}
      style={{ width: panelWidth, minWidth: 400, flexGrow: 1 }}
    >
      <StyledTabs
        activeKey={activeTab.toString()}
        onChange={handleChange}
        $height={height}
        items={tabList}
        renderTabBar={renderTabBar}
        destroyOnHidden={true}
      />
    </div>
  );
}

function MetricsPanel() {
  const metricsContainerRef = useRef(null);
  const dispatch = useDispatch();
  const metrics = useSelector(metricSelector);
  const activeTool = useAtomValue(activeToolAtom);
  const horizontalLayout = useAtomValue(horizontalLayoutAtom);
  const setMetricsContainerRef = useSetAtom(metricsContainerRefAtom);

  useQueryMetrics();

  useEffect(() => {
    setMetricsContainerRef(metricsContainerRef);
    return () => {
      setMetricsContainerRef(undefined);
    };
  }, [setMetricsContainerRef]);

  const {
    isFetchedAfterMount,
    isFetching: isMetricSeriesFetching,
    isError: isMetricSeriesError,
    error: metricQueryError,
    refetch,
  } = useFetchMetricSeries({ enabled: true });

  useEffect(() => {
    if (isFetchedAfterMount) {
      perfTrack();
    }
  }, [isFetchedAfterMount]);

  useAutoScroll({ enabled: !horizontalLayout });

  const { isFetching: isMetricFilterFetching } = useFetchMetricFilterSeries({ enabled: true });
  const { isFetching: isDoDFetching } = useFetchDod({ enabled: true });
  const { isFetching: isAIAlertsFetching } = useFetchAIAlerts({ enabled: true });
  const { isFetching: isManualAlertsFetching } = useFetchManualAlerts({ enabled: true });
  const { isFetching: isCompareSeriesFetching } = useFetchCompare({ enabled: true });

  useLoadingState({
    loading:
      isMetricFilterFetching ||
      isDoDFetching ||
      isAIAlertsFetching ||
      isManualAlertsFetching ||
      isCompareSeriesFetching,
  });

  // TODO: is loading indicator required?
  const { isFetching: isLiveSummaryFetching } = useLiveSummary({
    enabled: true,
  });

  const { chartSize } = useAtomValue(panelStyleAtom);
  const diagMode = useAtomValue(diagnosticsModeAtom);

  const sensors = useSensors(useSensor(PointerSensor));

  function handleDragEnd(event) {
    const { active, over } = event;
    const sourceIdx = metrics.indexOf(active.id);
    const destIdx = metrics.indexOf(over.id);

    // create new metric list
    const newMetricList = Array.from(metrics);
    const [reorderedItem] = newMetricList.splice(sourceIdx, 1);
    newMetricList.splice(destIdx, 0, reorderedItem);

    dispatch(setMetrics(newMetricList));
  }

  const errorMsg = useECOServerErrorMessage(metricQueryError);
  return (
    <>
      <div id="metric-chart-container" className={styles.loadingLayer}>
        <Retry
          isLoading={isMetricSeriesFetching}
          showError={isMetricSeriesError}
          fetchData={refetch}
          progress={true}
          showHeavyMetricMessage={isEiModule}
          errorMessage={errorMsg}
        >
          <MetricChartsWrapper
            key="metric-charts"
            style={getChartStyle(chartSize, diagMode)}
            horizontal={horizontalLayout}
            ref={metricsContainerRef}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <Charts activeTool={activeTool} />
            </DndContext>
            <ExportCompareFilters />
          </MetricChartsWrapper>
        </Retry>
      </div>
    </>
  );
}

const MetricsPanelContainer = () => {
  const showPanel = useAtomValue(metricPanelAtom);
  return showPanel ? (
    <ErrorBoundary>
      <PanelTabs />
    </ErrorBoundary>
  ) : null;
};

export default MetricsPanelContainer;
